import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import SocketIOClient from "socket.io-client";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { SOCKET_URL } from "../utils/constants";

const SocketContext = createContext();

const userFromStorage = () => {
  const raw = localStorage.getItem("user");
  if (raw == null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [balance, setBalance] = useState(null);
  const [cashback, setCashback] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [forceDisconnect, setForceDisconnect] = useState(false);
  const [status, setStatus] = useState({});
  const socketRef = useRef(null);
  const fingerprintRef = useRef(null);
  const emitGetbalanceRef = useRef(() => {});

  const loadFingerprint = useCallback(async () => {
    if (fingerprintRef.current) return fingerprintRef.current;
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    fingerprintRef.current = result.visitorId;
    return fingerprintRef.current;
  }, []);

  emitGetbalanceRef.current = async (client) => {
    const user = userFromStorage();
    const target = client || socketRef.current;
    if (!user || !target) return;
    let fingerprint = null;
    try {
      fingerprint = await loadFingerprint();
    } catch {
      fingerprint = null;
    }
    target.emit("Getbalance", {
      id: user.id,
      username: user.username,
      fingerprint,
    });
  };

  const attachClient = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
    }

    const client = SocketIOClient(SOCKET_URL, {
      reconnection: true,
    });
    socketRef.current = client;
    setSocket(client);

    client.on("connect", () => {
      emitGetbalanceRef.current(client);
    });
    client.on("status", (data) => {
      setStatus(data || {});
    });
    client.on("balance", (data) => {
      if (data?.force_disconnect) {
        setForceDisconnect(true);
        return;
      }
      if (data?.balance !== undefined) setBalance(data.balance);
      if (data?.cashback !== undefined) setCashback(data.cashback);
      if (data?.blocked !== undefined) setBlocked(data.blocked);
      setForceDisconnect(false);
    });

    return client;
  }, []);

  useEffect(() => {
    attachClient();
    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [attachClient]);

  const connectSocket = useCallback(() => {
    if (!socketRef.current) attachClient();
  }, [attachClient]);

  const disconnectSocket = useCallback(() => {
    setBalance(null);
    setCashback(null);
    setBlocked(false);
    setForceDisconnect(false);
    attachClient();
  }, [attachClient]);

  const acknowledgeForceDisconnect = useCallback(() => {
    setForceDisconnect(false);
    setBalance(null);
    setCashback(null);
    setBlocked(false);
  }, []);

  return (
    <SocketContext.Provider
      value={{
        socket,
        connectSocket,
        disconnectSocket,
        acknowledgeForceDisconnect,
        balance,
        cashback,
        blocked,
        forceDisconnect,
        status,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
export default SocketProvider;
